import React, { useEffect, useRef, useState } from "react";
import { Upload, FileText, CheckCircle2, AlertTriangle, Loader2, Building2, Newspaper, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { api, apiGet, formatDate } from "@/lib/api";
import ReportDownloadSettingsCard from "@/components/admin/ReportDownloadSettingsCard";
import IpoManagementCard from "@/components/admin/IpoManagementCard";

const CSE_UPLOAD_TYPES = [
  {
    key: "cse_daily",
    autoKey: "daily",
    icon: "📋",
    title: "CSE Daily Market Summary",
    description: "Official CSE daily report. Auto-downloaded weekdays 18:05 Asia/Colombo. Manual upload below as backup.",
    endpoint: "/admin/market-intelligence/upload-cse-daily",
    autoEndpoint: "/admin/market-intelligence/auto-download/daily",
  },
  {
    key: "cse_weekly",
    autoKey: "weekly",
    icon: "📋",
    title: "CSE Weekly Market Summary",
    description: "Auto-downloaded Friday 20:00 Asia/Colombo. Manual upload below as backup.",
    endpoint: "/admin/market-intelligence/upload-cse-weekly",
    autoEndpoint: "/admin/market-intelligence/auto-download/weekly",
  },
  {
    key: "cse_monthly",
    autoKey: "monthly",
    icon: "📋",
    title: "CSE Monthly Market Summary",
    description: "Auto-downloaded 1st of every month at 09:00. Manual upload below as backup.",
    endpoint: "/admin/market-intelligence/upload-cse-monthly",
    autoEndpoint: "/admin/market-intelligence/auto-download/monthly",
  },
  {
    key: "cse_quarterly",
    autoKey: null,
    icon: "📋",
    title: "CSE Quarterly Market Summary",
    description: "Manual upload only — CSE does not publish quarterly reports through their public API.",
    endpoint: "/admin/market-intelligence/upload-cse-quarterly",
  },
];

const BROKER_UPLOAD_TYPES = [
  {
    key: "broker_daily",
    icon: "📊",
    title: "Capital Trust Daily Market Watch",
    description: "Foreign flow details, dividend XD announcements, and broker market commentary.",
    endpoint: "/admin/market-intelligence/upload-broker-daily",
    note: "Uses Claude vision — requires LLM key budget.",
  },
  {
    key: "broker_stock_focus",
    icon: "📈",
    title: "Capital Trust Weekly Stock Focus",
    description: "Verified PE, EPS, ROE, BVPS, DPS for all CSE stocks (uses pdfplumber — no LLM cost).",
    endpoint: "/admin/market-intelligence/upload-broker-stock-focus",
  },
  {
    key: "broker_weekly_update",
    icon: "🌍",
    title: "Capital Trust Weekly Market Update",
    description: "Macro analysis, exchange rates, PMI, FX reserves, and economic news.",
    endpoint: "/admin/market-intelligence/upload-broker-weekly-update",
    note: "Uses Claude vision — requires LLM key budget.",
  },
];

const UploadCard = ({ type, status, autoStatus, onUploaded }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(type.endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setLastResult(data);
      toast.success(`${type.title} processed`);
      onUploaded?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(`Upload failed: ${detail}`);
      setLastResult({ error: detail });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleAutoDownload = async () => {
    if (!type.autoEndpoint) return;
    setAutoBusy(true);
    try {
      const { data } = await api.post(`${type.autoEndpoint}?force=true`, null, { timeout: 300000 });
      setLastResult(data);
      const sts = data?.last_run_status;
      if (sts === "ok") toast.success(`Auto-download OK — ${data.last_records_extracted || 0} rows extracted`);
      else if (sts === "no_new_report") toast.info("No new report available");
      else toast.error(`Auto-download status: ${sts || "error"}`);
      onUploaded?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Auto-download failed";
      toast.error(`Auto-download failed: ${detail}`);
    } finally {
      setAutoBusy(false);
    }
  };

  const lastDate = status?.latest;
  const count = status?.count ?? 0;
  const isAuto = !!type.autoKey;
  const auto = autoStatus || null;

  // Auto-download banner state
  let autoBanner = null;
  if (isAuto) {
    if (!auto || !auto.last_run_at) {
      autoBanner = (
        <div className="rounded-md bg-muted/40 border border-border px-2 py-1.5 text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Zap className="h-3 w-3" />
          Auto-download not yet run
        </div>
      );
    } else if (auto.last_run_status === "ok") {
      autoBanner = (
        <div className="rounded-md bg-success/10 border border-success/20 px-2 py-1.5 text-[11px] flex items-center gap-1.5 text-[hsl(var(--success))]" data-testid={`auto-status-${type.key}`}>
          <CheckCircle2 className="h-3 w-3" />
          <span className="text-foreground">
            Last auto: <strong>{formatDate(auto.last_run_at) || "—"}</strong>
            {" "}— {auto.last_records_matched || 0} matched / {auto.last_records_extracted || 0} extracted
            {auto.last_processed_date ? ` · report ${auto.last_processed_date}` : ""}
          </span>
        </div>
      );
    } else if (auto.last_run_status === "no_new_report") {
      autoBanner = (
        <div className="rounded-md bg-muted/40 border border-border px-2 py-1.5 text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Zap className="h-3 w-3" />
          Last auto: {formatDate(auto.last_run_at)} — no new report
        </div>
      );
    } else {
      autoBanner = (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-2 py-1.5 text-[11px] text-destructive flex items-start gap-1.5">
          <AlertTriangle className="h-3 w-3 mt-0.5" />
          <span>Auto-download FAILED ({auto.last_run_status}). {auto.last_error?.slice(0, 80)}</span>
        </div>
      );
    }
  }

  return (
    <Card data-testid={`upload-card-${type.key}`} className="flex flex-col h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="text-2xl leading-none">{type.icon}</span>
          <span className="flex-1">{type.title}</span>
          {isAuto && (
            <Badge variant="secondary" className="text-[10px] gap-0.5">
              <Zap className="h-2.5 w-2.5" /> Auto
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs leading-relaxed">
          {type.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="mt-auto space-y-3">
        {autoBanner}
        <div className="flex items-center gap-3 text-xs">
          <div>
            <div className="text-muted-foreground">Last record date</div>
            <div className="font-medium">{lastDate ? formatDate(lastDate) : "Never"}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-muted-foreground">Records</div>
            <div className="font-medium">{count || "—"}</div>
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={handleFile}
          data-testid={`upload-input-${type.key}`}
        />
        <div className="flex gap-2">
          {isAuto && type.autoEndpoint && (
            <Button
              variant="default"
              size="sm"
              className="flex-1"
              onClick={handleAutoDownload}
              disabled={autoBusy || busy}
              data-testid={`auto-download-button-${type.key}`}
            >
              {autoBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
              {autoBusy ? "Downloading…" : "Run auto-download"}
            </Button>
          )}
          <Button
            variant={isAuto ? "outline" : "default"}
            size="sm"
            className="flex-1"
            onClick={() => inputRef.current?.click()}
            disabled={busy || autoBusy}
            data-testid={`upload-button-${type.key}`}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {busy ? "Processing…" : "Upload PDF"}
          </Button>
        </div>
        {type.note && (
          <div className="text-[11px] text-muted-foreground italic">{type.note}</div>
        )}
        {lastResult && !lastResult.error && (
          <div className="rounded-md bg-success/10 border border-success/20 p-2 text-xs space-y-1" data-testid={`upload-result-${type.key}`}>
            <div className="flex items-center gap-1.5 font-medium text-[hsl(var(--success))]">
              <CheckCircle2 className="h-3.5 w-3.5" /> Success
            </div>
            {Object.entries(lastResult).slice(0, 6).map(([k, v]) => {
              if (k === "ok" || k === "extracted" || k === "unmatched_companies") return null;
              if (typeof v === "object" || Array.isArray(v)) return null;
              return (
                <div key={k} className="flex justify-between">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-mono">{String(v)}</span>
                </div>
              );
            })}
            {lastResult.unmatched_companies?.length > 0 && (
              <div className="text-amber-600 dark:text-amber-400 text-[11px] mt-1">
                {lastResult.unmatched_companies.length} companies could not be matched to symbols.
              </div>
            )}
          </div>
        )}
        {lastResult?.error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-2 text-xs text-destructive flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
            <span>{lastResult.error}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MarketIntelligenceUploadPage = () => {
  const [status, setStatus] = useState(null);

  const refresh = async () => {
    try {
      const data = await apiGet("/admin/market-intelligence/status");
      setStatus(data);
    } catch (err) {
      console.warn("status fetch failed", err);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="space-y-8 max-w-7xl" data-testid="market-intel-admin-page">
      <div>
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" />
          Market Intelligence — Upload
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Upload official CSE reports and broker research PDFs. Data feeds the public Market Intelligence dashboard, the screener, and stock detail pages.
        </p>
      </div>

      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-semibold">Section A — CSE Official Reports</h2>
            <p className="text-xs text-muted-foreground">Primary source. Official reports from the Colombo Stock Exchange. Recommended.</p>
          </div>
          <Badge variant="secondary" className="ml-auto">Primary</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CSE_UPLOAD_TYPES.map((t) => (
            <UploadCard
              key={t.key}
              type={t}
              status={status?.[t.key]}
              autoStatus={t.autoKey ? status?.auto_download?.[t.autoKey] : null}
              onUploaded={refresh}
            />
          ))}
        </div>
      </section>

      <Separator />

      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <Newspaper className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-semibold">Section B — Broker Research</h2>
            <p className="text-xs text-muted-foreground">Optional supplementary data — broker research adds fundamentals and macro context not in CSE reports.</p>
          </div>
          <Badge variant="outline" className="ml-auto">Secondary</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {BROKER_UPLOAD_TYPES.map((t) => (
            <UploadCard
              key={t.key}
              type={t}
              status={status?.[t.key]}
              onUploaded={refresh}
            />
          ))}
        </div>
      </section>

      <Separator />

      {/* ------------------------------------------------------------
          Section C — Auto-Download Settings
          Controls the nightly CSE annual/interim report discovery job.
          Default policy is "Annual only, do NOT auto-analyse" so a
          discovery run can't silently rack up a 200+ report Claude bill.
          The 20:00 cron still processes the top-20 annual BUY stocks
          regardless of the auto-analyse toggle below.
         ------------------------------------------------------------ */}
      <section className="space-y-3" data-testid="auto-download-settings-section">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-semibold">Section C — Company Report Auto-Download</h2>
            <p className="text-xs text-muted-foreground">
              Policy for the nightly cse.lk annual / interim report discovery job (18:30 SLT).
            </p>
          </div>
          <Badge variant="outline" className="ml-auto">Admin policy</Badge>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ReportDownloadSettingsCard />
          <Card className="bg-muted/40 border-dashed">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">How this interacts with the analysis cron</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-1.5">
              <div>
                • <strong>Discovery cron</strong> (18:30 SLT): polls cse.lk for new filings.
                Respects the “Report types” filter above.
              </div>
              <div>
                • <strong>Analysis cron</strong> (20:00 SLT): processes the top-20{" "}
                <em>pending annual reports</em> sorted by composite score DESC — so
                high-conviction BUY stocks get their Claude analysis first.
                <em> Runs regardless of the auto-analyse toggle.</em>
              </div>
              <div>
                • <strong>Manual analysis</strong>: admin can always click
                “Analyse Now” on any pending report from the stock detail page.
              </div>
              <div>
                • <strong>Auto-analyse ON:</strong> also runs Claude on every
                download immediately (in addition to the nightly cron). Expensive
                during backfills — keep OFF unless you specifically want that.
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Card className="bg-muted/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Data source priority</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-1">
          <div>• <strong>Prices &amp; market stats:</strong> CSE Official takes priority over broker.</div>
          <div>• <strong>Fundamentals (PE, EPS, ROE):</strong> Broker (Capital Trust) Stock Focus takes priority over scraped TradingView data.</div>
          <div>• <strong>Tax invariant:</strong> All dividends store gross + 10% WHT + net. There is NO capital gains tax on CSE shares.</div>
        </CardContent>
      </Card>

      <Separator />

      {/* ------------------------------------------------------------
          Section D — IPO Management (Phase 8)
          Admin flow for registering CSE IPOs, uploading the prospectus
          PDF, and triggering the ipo_analysis_service pipeline. Each
          IPO ends up on the public /ipo page once analysed.
         ------------------------------------------------------------ */}
      <section className="space-y-3" data-testid="admin-ipo-management-section">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-semibold">Section D — IPO Management</h2>
            <p className="text-xs text-muted-foreground">
              Register new CSE IPOs, upload prospectus PDFs, and track post-listing performance.
            </p>
          </div>
          <Badge variant="outline" className="ml-auto">Admin only</Badge>
        </div>
        <IpoManagementCard />
      </section>
    </div>
  );
};

export default MarketIntelligenceUploadPage;
